/** Sparkles when something is built, grows, is dug or falls: one Points cloud with a small shader. */
import type { Grid } from '@bramvasterd/terrella-rules';
import * as THREE from 'three';

const COUNT = 400;
const UP = new THREE.Vector3(0, 1, 0);
const X = new THREE.Vector3(1, 0, 0);

export interface Sparks {
  burst(grid: Grid, tile: number, radius: number, top: number, color: number, count: number): void;
  /** A burst at a point on the planet (a unit vector), such as a corner. */
  burstAt(
    x: number,
    y: number,
    z: number,
    radius: number,
    top: number,
    color: number,
    count: number,
  ): void;
  /** Advance; returns whether any spark is alive. */
  step(dt: number): boolean;
  setScale(scale: number): void;
}

export function createSparks(parent: THREE.Object3D, reduced: boolean): Sparks {
  const pos = new Float32Array(COUNT * 3);
  const col = new Float32Array(COUNT * 3);
  const vel = new Float32Array(COUNT * 3);
  const life = new Float32Array(COUNT);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(col, 3));
  geometry.setAttribute('life', new THREE.BufferAttribute(life, 1));
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { scale: { value: 600 } },
    vertexShader:
      'attribute float life;attribute vec3 color;varying float vL;varying vec3 vC;uniform float scale;void main(){vL=life;vC=color;vec4 mv=modelViewMatrix*vec4(position,1.0);gl_PointSize=max(0.0,life)*0.22*scale/-mv.z;gl_Position=projectionMatrix*mv;}',
    fragmentShader:
      'varying float vL;varying vec3 vC;void main(){vec2 d=gl_PointCoord-0.5;float a=smoothstep(0.5,0.1,length(d))*clamp(vL*1.6,0.0,1.0);if(a<0.01)discard;gl_FragColor=vec4(vC,a);}',
  });
  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;
  parent.add(points);
  let next = 0;
  const c = new THREE.Vector3();
  const t1 = new THREE.Vector3();
  const t2 = new THREE.Vector3();
  const v = new THREE.Vector3();
  const color = new THREE.Color();

  const burstAt = (
    x: number,
    y: number,
    z: number,
    radius: number,
    top: number,
    hex: number,
    count: number,
  ): void => {
    if (reduced) count = Math.min(count, 6);
    c.set(x, y, z);
    const a = Math.abs(c.y) < 0.9 ? UP : X;
    t1.crossVectors(a, c).normalize();
    t2.crossVectors(c, t1).normalize();
    color.set(hex);
    const bx = c.x * (radius + top + 0.1);
    const by = c.y * (radius + top + 0.1);
    const bz = c.z * (radius + top + 0.1);
    for (let k = 0; k < count; k++) {
      const j = next;
      next = (next + 1) % COUNT;
      pos[j * 3] = bx;
      pos[j * 3 + 1] = by;
      pos[j * 3 + 2] = bz;
      const ang = Math.random() * 6.283;
      const s = 0.5 + Math.random() * 1.1;
      const u = 1 + Math.random() * 1.5;
      v.copy(c)
        .multiplyScalar(u)
        .addScaledVector(t1, Math.cos(ang) * s)
        .addScaledVector(t2, Math.sin(ang) * s);
      vel[j * 3] = v.x;
      vel[j * 3 + 1] = v.y;
      vel[j * 3 + 2] = v.z;
      life[j] = 1;
      col[j * 3] = color.r;
      col[j * 3 + 1] = color.g;
      col[j * 3 + 2] = color.b;
    }
  };

  return {
    burst(grid, tile, radius, top, hex, count) {
      if (tile < 0 || tile >= grid.count) return;
      burstAt(
        grid.centers[tile * 3] ?? 0,
        grid.centers[tile * 3 + 1] ?? 0,
        grid.centers[tile * 3 + 2] ?? 0,
        radius,
        top,
        hex,
        count,
      );
    },
    burstAt,
    step(dt) {
      let any = false;
      for (let j = 0; j < COUNT; j++) {
        const l = life[j] ?? 0;
        if (l <= 0) continue;
        any = true;
        life[j] = Math.max(0, l - dt * 1.3);
        pos[j * 3] = (pos[j * 3] ?? 0) + (vel[j * 3] ?? 0) * dt;
        pos[j * 3 + 1] = (pos[j * 3 + 1] ?? 0) + (vel[j * 3 + 1] ?? 0) * dt;
        pos[j * 3 + 2] = (pos[j * 3 + 2] ?? 0) + (vel[j * 3 + 2] ?? 0) * dt;
        vel[j * 3] = (vel[j * 3] ?? 0) * 0.93;
        vel[j * 3 + 1] = (vel[j * 3 + 1] ?? 0) * 0.93;
        vel[j * 3 + 2] = (vel[j * 3 + 2] ?? 0) * 0.93;
      }
      if (any) {
        for (const name of ['position', 'color', 'life']) {
          const attribute = geometry.attributes[name];
          if (attribute) attribute.needsUpdate = true;
        }
      }
      return any;
    },
    setScale(scale) {
      const uniform = material.uniforms['scale'];
      if (uniform) uniform.value = scale;
    },
  };
}
